// Client orchestration of the zero-knowledge auth + vault flow (proven by
// test/zk-auth-e2e.mjs). Runs in the browser; all crypto is local (keyvault.js),
// only ciphertext + one-way login keys cross the wire (vault-api.js).
import * as api from './vault-api.js';
import * as kv from './keyvault.js';

const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
export const basicAuth = (email, secret) => 'Basic ' + btoa(unescape(encodeURIComponent(`${email}:${secret}`)));

// Fetch the current server salt for an email and derive the one-way login key.
async function loginKeyFor(email, secret, which = 'login') {
  const path = which === 'recover' ? '/auth/v1/recover/begin' : '/auth/v1/login/begin';
  const r = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
  const { salt } = await r.json();
  return kv.deriveLoginKey(secret, unb64(salt));
}

// Attempt login. Returns:
//   { unlocked: { mailCred, pqc, loginKey, token } }  - vault exists, password correct
//   { needsBootstrap: { basic } }                     - password correct but no vault (migrate/first)
// Throws Error('...') on wrong password / rate-limit.
export async function login(email, password, validateBasic) {
  const token = await api.loginWithPassword(email, password);
  if (token) {
    const vault = await api.getVault('Bearer ' + token);
    if (vault) {
      const { bundle } = await kv.openVault(vault, 'password', password); // throws on wrong pw
      return { unlocked: { mailCred: bundle.mailCred, pqc: bundle.pqc, loginKey: await loginKeyFor(email, password), token } };
    }
  }
  const basic = basicAuth(email, password);
  if (!(await validateBasic(basic))) throw new Error('Incorrect email or password');
  return { needsBootstrap: { basic } };
}

// Create a vault for a verified account (migration / first device). `pqc` is the
// key bundle the caller recovered (old escrow / local) or freshly provisioned.
export async function bootstrap(email, password, basic, pqc) {
  const mailCred = api.newMailCred();
  const recoveryCode = kv.newRecoveryCode();
  const vault = await kv.createVault({ pqc, mailCred }, { password, recoveryCode });
  await api.putVault(basic, vault);        // old password still valid
  await api.setMailCred(basic, mailCred);  // LAST Basic call: swaps the Stalwart password
  const token = await api.loginWithPassword(email, password);
  return { mailCred, pqc, loginKey: await loginKeyFor(email, password), token, recoveryCode };
}

// Silently re-mint a session token on refresh using the stored (one-way) login key.
export async function sessionFromLoginKey(email, loginKey, which = 'login') {
  const path = which === 'recover' ? '/auth/v1/recover/finish' : '/auth/v1/login/finish';
  const r = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, loginKey }) });
  if (!r.ok) return null;
  return (await r.json()).token;
}

// Forgot password: unlock with the recovery code, set a NEW password, re-key.
export async function recoverWithCode(email, recoveryCode, newPassword) {
  const code = kv.normalizeCode(recoveryCode);
  const token = await sessionFromLoginKey(email, await loginKeyFor(email, code, 'recover'), 'recover');
  if (!token) throw new Error('Incorrect recovery code');
  const vault = await api.getVault('Bearer ' + token);
  const { bundle, mkRaw } = await kv.openVault(vault, 'recovery', code);
  await kv.changePassword(vault, mkRaw, newPassword);
  await api.putVault('Bearer ' + token, vault);
  return { mailCred: bundle.mailCred, pqc: bundle.pqc, loginKey: await loginKeyFor(email, newPassword), token };
}

// Change password from within a session (needs the current password to open MK).
export async function changePassword(email, token, oldPassword, newPassword) {
  const vault = await api.getVault('Bearer ' + token);
  const { mkRaw } = await kv.openVault(vault, 'password', oldPassword); // throws on wrong old pw
  await kv.changePassword(vault, mkRaw, newPassword);
  await api.putVault('Bearer ' + token, vault);
  return loginKeyFor(email, newPassword);
}

// Optional passphrase (mode B): add/change/remove. `password` opens MK.
export async function setPassphrase(token, password, passphrase) {
  const vault = await api.getVault('Bearer ' + token);
  const { mkRaw } = await kv.openVault(vault, 'password', password);
  await kv.setUnlocker(vault, mkRaw, 'passphrase', passphrase);
  await api.putVault('Bearer ' + token, vault);
}
export async function removePassphrase(token, password) {
  const vault = await api.getVault('Bearer ' + token);
  await kv.openVault(vault, 'password', password); // verify
  kv.removeUnlocker(vault, 'passphrase');
  await api.putVault('Bearer ' + token, vault);
}
export async function hasPassphrase(token) {
  const st = await api.vaultStatus('Bearer ' + token);
  return (st.unlockers || []).includes('passphrase');
}

// Rotate the recovery code (e.g., if the old one may be exposed). Returns the new
// code to show the user once. Needs the current password to open MK.
export async function regenerateRecovery(token, password) {
  const vault = await api.getVault('Bearer ' + token);
  const { mkRaw } = await kv.openVault(vault, 'password', password);
  const code = kv.newRecoveryCode();
  await kv.setRecoveryCode(vault, mkRaw, code);
  await api.putVault('Bearer ' + token, vault);
  return code;
}
